import { DateTableColumn } from 'src/app/core/models/date-table-column';
import { HttpErrorResponse } from '@angular/common/http';
import { BookingArriveRequest } from './bean/booking-arrive-request';
import { ChangeDetectorRef, Component, Injector, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { BarcodeFormat } from '@zxing/library';
import { WebcamImage, WebcamInitError, WebcamUtil } from 'ngx-webcam';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { SessionBaseComponent } from 'src/app/shared/components/session.base.component';
import { BookingArriveService } from '../../services/booking-arrive.service';
import { Dialog } from 'primeng/dialog';
import { ProofOfDeliveryBean } from './bean/proof-of-delivery';
import { Chips } from 'primeng/chips';
import { PermissionConstants } from 'src/app/core/constants/permission-constants.constant';

export enum DisplayMode {
  Desktop,
  Mobile
}

export enum TabMenu {
  BookingArrive,
  DOArrive
}

@Component({
  selector: 'laas-booking-arrive',
  templateUrl: './booking-arrive.component.html',
  styleUrls: ['./booking-arrive.component.scss'],
  encapsulation: ViewEncapsulation.None
})

export class BookingArriveComponent extends SessionBaseComponent implements OnInit {

  readonly viewPermissionCode = PermissionConstants.BookingArrive;
  readonly DOC_REFERENCE_BOOKING = 'Booking'
  readonly DOC_REFERENCE_DELIVERY_ORDER = 'DeliveryOrder'
  readonly DISPLAY_MODE_ENUM = DisplayMode;
  readonly MOBILE_MODE_WINDOW_WIDTH = 640;
  displayMode: DisplayMode = DisplayMode.Desktop;
  tabMenuActiveIndex: number = TabMenu.BookingArrive;
  currentLang: string = this.laasSetting.language;
  @ViewChild('bookingNoChipsOfBookingArrive') bookingNoChipsOfBookingArrive!: Chips
  bookingNoOfBookingArrive: string[] = []
  photoDataUrlOfBookingArrive: string | null = null

  //#region DO Arrive

  @ViewChild('bookingNoChipsOfDOArrive') bookingNoChipsOfDOArrive!: Chips
  bookingNoOfDOArrive: string[] = []
  currentRowIndexOfCamera: number = 0
  columnsOfPODTable: DateTableColumn[] = [
    { field: 'doNo', header: 'BookingArrive.PODTable.DoNo' },
    { field: 'photoUploadButton', header: 'BookingArrive.PODTable.PhotoUploadButton' },
    { field: 'photo', header: 'BookingArrive.PODTable.Photo' }
  ]
  doNos: string[] = []
  isPODTableLoading: boolean = false
  rowsPerPageOfPODTable: number = 5
  rowsPerPageOptionsOfPODTable: number[] = [5, 10, 20]
  photoDataUrlOfDOArrive: (string | null)[] = []

  //#endregion DO Arrive

  // if close dialog before camera standby, will keep the camera open
  // so keep loading before camera standby

  //#region Scanner

  @ViewChild('scannerDialog') scannerDialog!: Dialog
  showScannerDialog: boolean = false
  isScannerLoading: boolean = false
  showScanner: boolean = false
  torchEnabled: boolean = false
  torchAvailable: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)
  availableDevices: MediaDeviceInfo[] = [];
  currentScanner: MediaDeviceInfo | undefined

  formatsEnabled: BarcodeFormat[] = [
    BarcodeFormat.CODE_128,
    BarcodeFormat.DATA_MATRIX,
    BarcodeFormat.EAN_13
  ];

  //#endregion Scanner

  //#region Camera

  @ViewChild('cameraDialog') cameraDialog!: Dialog
  showCameraDialog: boolean = false
  isCameraLoading: boolean = false
  showCamera: boolean = false
  isMultiCamerasAvailable: boolean = false
  private trigger: Subject<void> = new Subject<void>();
  // switch to next / previous / specific webcam;
  // true/false: forward/backwards, string: deviceId
  private nextWebcam: Subject<boolean | string> = new Subject<boolean | string>();

  //#endregion CamerascannerDialog

  constructor(
    private injector: Injector,
    private changeDetector: ChangeDetectorRef,
    private bookingArriveService: BookingArriveService
  ) {
    super(injector)
    let windowResizeEventListener = this.onWindowResize.bind(this)
    window.addEventListener('resize', windowResizeEventListener)
  }

  async ngOnInit(): Promise<void> {
    await super.ngOnInit();
    if (window.innerWidth <= this.MOBILE_MODE_WINDOW_WIDTH) {
      this.changeToMobileMode();
    }
    this.getAvailableVideoInput()
  }

  private onWindowResize() {
    if (window.innerWidth <= this.MOBILE_MODE_WINDOW_WIDTH) {
      this.changeToMobileMode();
    }
    else {
      this.changeToDesktopMode();
    }
  }

  private changeToMobileMode() {
    this.displayMode = DisplayMode.Mobile
    if (this.showScannerDialog) {
      this.maximizeDialog(this.scannerDialog)
    }

    if (this.showCameraDialog) {
      this.maximizeDialog(this.cameraDialog)
    }
  }

  private changeToDesktopMode() {
    this.displayMode = DisplayMode.Desktop
    if (this.showScannerDialog) {
      this.unmaximizeDialog(this.scannerDialog)
    }

    if (this.showCameraDialog) {
      this.unmaximizeDialog(this.cameraDialog)
    }
  }

  isInDesktopMode(): boolean {
    return this.displayMode === this.DISPLAY_MODE_ENUM.Desktop;
  }

  private maximizeDialog(dialog: Dialog) {
    dialog.maximized = false
    dialog.modal = false
    dialog.maximize()
    dialog.disableModality()
  }

  private unmaximizeDialog(dialog: Dialog) {
    dialog.maximized = true
    dialog.modal = true
    dialog.maximize()
    dialog.disableModality()
  }

  private getAvailableVideoInput() {
    WebcamUtil.getAvailableVideoInputs()
      .then((mediaDevices: MediaDeviceInfo[]) => {
        this.isMultiCamerasAvailable = mediaDevices && mediaDevices.length > 1;
      });
  }

  enterBookingNo(event: Event) {
    let isGetDoNosOfBooking: boolean =
      (event as KeyboardEvent).key === "Enter"
      && this.tabMenuActiveIndex == TabMenu.DOArrive
      && this.checkIfBookingNoIsFilled(this.bookingNoOfDOArrive[0])

    if (isGetDoNosOfBooking) {
      this.getDoNosOfBooking(this.bookingNoOfDOArrive[0])
    }
  }

  async uploadPhoto(photoInput: HTMLInputElement, rowIndex: number) {
    let podPhoto = photoInput.files ? photoInput.files[0] : null
    if (podPhoto) {
      let photoDataUrl = await this.convertFileToDataUrl(podPhoto)

      if (this.tabMenuActiveIndex == TabMenu.BookingArrive) {
        this.photoDataUrlOfBookingArrive = photoDataUrl
      }
      else {
        this.photoDataUrlOfDOArrive[rowIndex] = photoDataUrl!
      }
    }

    photoInput.value = ''
  }

  removePhoto(rowIndex: number) {
    if (this.tabMenuActiveIndex == TabMenu.BookingArrive) {
      this.photoDataUrlOfBookingArrive = null
    }
    else {
      this.photoDataUrlOfDOArrive[rowIndex] = null
    }
  }

  onBookingArriveButtonClick() {
    let bookingNo = this.tabMenuActiveIndex == TabMenu.BookingArrive ?
      this.bookingNoOfBookingArrive[0] : this.bookingNoOfDOArrive[0]

    let doBookingArrive: boolean = this.checkIfBookingNoIsFilled(bookingNo) && this.checkIfAllDOGotPOD()

    if (!doBookingArrive) {
      return
    }

    this.checkIfBookingIsArrive(bookingNo)
  }

  onResetButtonClick() {
    if (this.tabMenuActiveIndex == TabMenu.BookingArrive) {
      this.bookingNoOfBookingArrive = []
    }
    else {
      this.bookingNoOfDOArrive = []
    }
    this.clearValue()
  }

  private checkIfAllDOGotPOD() {
    if (this.tabMenuActiveIndex == TabMenu.DOArrive) {
      let filteredPhotaDataUrl = this.photoDataUrlOfDOArrive.filter(dataUrl => dataUrl != undefined)
      let isShowWarningMessage = filteredPhotaDataUrl.length != 0 && filteredPhotaDataUrl.length != this.photoDataUrlOfDOArrive.length
      if (isShowWarningMessage) {
        let warningMessage = this.translateService.instant('BookingArrive.Warning.AllDOMustHavePOD')
        this.toastService.warn(warningMessage)
        return false;
      }
    }

    return true
  }

  private checkIfBookingNoIsFilled(bookingNo: string): boolean {
    if (!bookingNo) {
      this.toastService.warn(this.translateService.instant('BookingArrive.Warning.BookingNoIsEmpty'))
      return false
    }
    return true
  }

  private checkIfBookingIsArrive(bookingNo: string) {
    // Upload POD first is because need to make sure forwarder will keep upload POD if upload POD failed
    this.loader.show()
    this.bookingArriveService.checkIfBookingArrive(bookingNo).subscribe({
      next: (isArrive: boolean) => {
        this.loader.hide()
        if (isArrive) {
          this.showKeepUploadPODConfirmDialog(isArrive)
          return
        }
        this.makeArriveConfirm(isArrive)
      },
      error: (response: HttpErrorResponse) => {
        let errorMsg = this.translateService.instant("BookingArrive.Error.PODUploadFail")
        this.showCallApiErrorMessage(response, errorMsg)
      }
    })
  }

  private showKeepUploadPODConfirmDialog(isArrive: boolean) {
    this.confirmationService.confirm({
      header: this.translateService.instant('Common.Dialog.Header'),
      message: this.translateService.instant('BookingArrive.ConfirmDialog.KeepUploadPOD'),
      icon: 'pi pi-info-circle',
      accept: () => {
        this.makeArriveConfirm(isArrive)
      }
    })
  }

  private makeArriveConfirm(isArrive: boolean) {
    this.loader.show()
    if (this.tabMenuActiveIndex == TabMenu.BookingArrive) {
      this.makeArriveConfirmByBooking(isArrive)
    }
    else {
      this.makeArriveConfirmByDO(isArrive)
    }
  }

  private async makeArriveConfirmByBooking(isArrive: boolean) {
    let bookingNo = this.bookingNoOfBookingArrive[0]
    let podList: ProofOfDeliveryBean[] = []

    if (this.photoDataUrlOfBookingArrive) {
      let podFileBase64 = this.photoDataUrlOfBookingArrive?.split(',')[1]
      let podBean: ProofOfDeliveryBean = this.createProofOfDeliveryBean(
        `${bookingNo}`, bookingNo, podFileBase64
      )
      podList.push(podBean)
    }

    let bookingArriveRequest: BookingArriveRequest = this.createBookingArriveRequest(bookingNo, this.DOC_REFERENCE_BOOKING, podList)
    let isKeepMakeBookingArrive: boolean = await this.uploadPOD(isArrive, bookingArriveRequest)

    if (isKeepMakeBookingArrive) {
      this.makeBookingArrive(bookingArriveRequest)
    }
    else {
      let successMessage = this.translateService.instant("BookingArrive.Success.PODUploadSuccess")
      this.showCallApiSuccessMessage(successMessage)
      this.clearValue()
    }
  }

  private getDoNosOfBooking(bookingNo: string) {
    this.isPODTableLoading = true
    this.bookingArriveService.getDoNosOfBooking(bookingNo).subscribe({
      next: (doNos: string[]) => {
        this.doNos = doNos
        this.photoDataUrlOfDOArrive = new Array(doNos.length).fill(null)
        this.isPODTableLoading = false
      },
      error: (response: HttpErrorResponse) => {
        let errorMsg = this.translateService.instant("BookingArrive.Error.NotFoundAnyDoNo")
        this.showCallApiErrorMessage(response, errorMsg)
      }
    })
  }

  private async makeArriveConfirmByDO(isArrive: boolean) {
    let bookingNo = this.bookingNoOfDOArrive[0]
    let isKeepMakeBookingArrive: boolean = false
    let bookingArriveRequest: BookingArriveRequest | undefined

    for (let i = 0; i < this.doNos.length; i++) {
      let podList: ProofOfDeliveryBean[] = []

      if (this.photoDataUrlOfDOArrive[i]) {
        let podFileBase64 = this.photoDataUrlOfDOArrive[i]?.split(',')[1]
        let podBean: ProofOfDeliveryBean = this.createProofOfDeliveryBean(
          `${bookingNo}_${this.doNos[i]}`, this.doNos[i], podFileBase64!
        )
        podList.push(podBean)
      }
      bookingArriveRequest = this.createBookingArriveRequest(bookingNo, this.DOC_REFERENCE_DELIVERY_ORDER, podList)
      isKeepMakeBookingArrive = await this.uploadPOD(isArrive, bookingArriveRequest)
    }

    if (isKeepMakeBookingArrive) {
      if (!bookingArriveRequest) {
        bookingArriveRequest = this.createBookingArriveRequest(bookingNo, this.DOC_REFERENCE_DELIVERY_ORDER, [])
      }
      this.makeBookingArrive(bookingArriveRequest)
    }
    else {
      let successMessage = this.translateService.instant("BookingArrive.Success.PODUploadSuccess")
      this.showCallApiSuccessMessage(successMessage)
      this.clearValue()
    }
  }

  private async uploadPOD(isArrive: boolean, bookingArriveRequest: BookingArriveRequest): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.bookingArriveService.uploadPOD(bookingArriveRequest).subscribe({
        next: () => {
          if (isArrive) {
            resolve(false)
          }

          resolve(true)
        },
        error: (response: HttpErrorResponse) => {
          let errorMsg = this.translateService.instant("BookingArrive.Error.PODUploadFail")
          this.showCallApiErrorMessage(response, errorMsg)
          resolve(false)
        }
      })
    })
  }

  private makeBookingArrive(bookingArriveRequest: BookingArriveRequest) {
    this.bookingArriveService.makeBookingArrive(bookingArriveRequest).subscribe({
      next: () => {
        this.clearValue()
        let successMessage = this.translateService.instant("BookingArrive.Success.BookingArriveConfirmSuccess")
        this.showCallApiSuccessMessage(successMessage)
      },
      error: (response: HttpErrorResponse) => {
        let errorMsg = this.translateService.instant("BookingArrive.Error.MakeBookingArriveFail")
        this.showCallApiErrorMessage(response, errorMsg)
      }
    })
  }

  private showCallApiSuccessMessage(successMessage: string) {
    this.loader.hide()
    this.toastService.success(successMessage)
  }

  private showCallApiErrorMessage(response: HttpErrorResponse, defaultMessage: string) {
    this.loader.hide()
    if (!response.error) {
      this.toastErrorMessage(response.error)
    }
    else {
      this.toastService.error(defaultMessage)
    }
  }

  private createProofOfDeliveryBean(fileName: string, referenceKey: string, podFileBase64: string): ProofOfDeliveryBean {
    let podBean = new ProofOfDeliveryBean()
    podBean.fileName = fileName
    podBean.referenceKey = referenceKey
    podBean.podFile = podFileBase64;
    return podBean
  }

  private createBookingArriveRequest(bookingNo: string, docReference: string, podList: ProofOfDeliveryBean[]): BookingArriveRequest {
    let bookingArriveRequest = new BookingArriveRequest()
    bookingArriveRequest.bookingNo = bookingNo
    bookingArriveRequest.userId = this.userService.userInfo.userName
    bookingArriveRequest.facility = this.laasSetting.facility
    bookingArriveRequest.docReference = docReference
    bookingArriveRequest.podList = podList
    return bookingArriveRequest
  }

  private async convertFileToDataUrl(file: File): Promise<string | null> {
    return new Promise((resolve, reject) => {
      let fileReader = new FileReader();
      fileReader.readAsDataURL(file);
      fileReader.onload = () => {
        let result = fileReader.result as string;
        if (result) {
          resolve(result);
        }
      }
      fileReader.onerror = () => reject(null)
    })
  }

  //#region Scanner

  openScannerDialog() {
    this.showScannerDialog = true
    this.showScannerLoading()

    if (this.displayMode == DisplayMode.Mobile) {
      this.maximizeDialog(this.scannerDialog)
    }
  }

  closeScannerDialog() {
    this.showScannerDialog = false
    this.torchEnabled = false
  }

  onCloseScannerDialog() {
    this.closeScanner()
    this.closeTorch()
  }

  openScanner() {
    this.showScanner = true
    this.changeDetector.detectChanges()
  }

  closeScanner() {
    this.showScanner = false
  }

  closeTorch() {
    this.torchEnabled = false
  }

  checkPermission(hasPermission: boolean) {
    if (!hasPermission) {
      this.showCameraErrorMessage(this.translateService.instant('BookingArrive.CameraError.PermissionDenied'))
      this.hideScannerLoading()
      this.closeScannerDialog()
    }
  }

  findScanner(devices: MediaDeviceInfo[]): void {
    let hasDevices = devices && devices.length > 0
    if (!hasDevices) {
      this.showCameraErrorMessage(this.translateService.instant('BookingArrive.CameraError.CameraNotFound'))
      this.closeScannerDialog()
      return
    }

    this.availableDevices = devices;
  }

  onScannerChange(device: MediaDeviceInfo) {
    // when closing dialog, this function will be invoke
    // but the scanner dialog html is destroyed, so change showScannerLoading will trigger error
    // At the time, the value of device will be null, so use if statement to avoid error
    if (device) {
      this.hideScannerLoading()
    }

    this.currentScanner = device || undefined
  }

  onSelectedScannerChange() {
    this.torchEnabled = false
    this.showScannerLoading()
    this.changeDetector.detectChanges()
  }

  toggleTorch() {
    this.torchEnabled = !this.torchEnabled
  }

  onTorchCompatible(isCompatible: boolean): void {
    this.torchAvailable.next(isCompatible || false);
  }

  onScanFailure() {
    // For hide loading when switch scanner
    this.hideScannerLoading()
  }

  afterScan(scannedString: string) {
    if (this.tabMenuActiveIndex == TabMenu.BookingArrive) {
      this.bookingNoChipsOfBookingArrive.removeItem(new MouseEvent('RemoveItem'), 0)
      this.bookingNoChipsOfBookingArrive.addItem(new InputEvent('Scanner'), scannedString, true)
    }
    else {
      this.bookingNoChipsOfDOArrive.removeItem(new MouseEvent('RemoveItem'), 0)
      this.bookingNoChipsOfDOArrive.addItem(new InputEvent('Scanner'), scannedString, true)
    }

    if (this.tabMenuActiveIndex == TabMenu.DOArrive) {
      this.getDoNosOfBooking(scannedString)
    }

    this.closeScannerDialog();
  }

  private showScannerLoading() {
    this.isScannerLoading = true
  }

  private hideScannerLoading() {
    this.isScannerLoading = false
  }

  //#endregion Scanner

  //#region Camera

  get triggerObservable(): Observable<void> {
    return this.trigger.asObservable();
  }

  get nextWebcamObservable(): Observable<boolean | string> {
    return this.nextWebcam.asObservable();
  }

  openCameraDialog(rowIndex: number) {
    this.showCameraDialog = true
    this.showCameraLoading()

    if (this.tabMenuActiveIndex == TabMenu.DOArrive) {
      this.currentRowIndexOfCamera = rowIndex
    }

    if (this.displayMode == DisplayMode.Mobile) {
      this.maximizeDialog(this.cameraDialog)
    }
  }

  closeCameraDialog() {
    this.showCameraDialog = false
  }

  openCamera() {
    this.showCamera = true
  }

  closeCamera() {
    this.showCamera = false;
  }

  handleInitError(error: WebcamInitError) {
    this.showCameraErrorMessage(error.message)
    this.hideCameraLoading()
    this.closeCameraDialog()
  }

  afterCameraStandby() {
    this.hideCameraLoading()
  }

  triggerSnapshot(): void {
    this.trigger.next();
    this.closeCameraDialog();
  }

  handleImage(webcamImage: WebcamImage) {
    if (this.tabMenuActiveIndex == TabMenu.BookingArrive) {
      this.photoDataUrlOfBookingArrive = webcamImage.imageAsDataUrl
    }
    else {
      this.photoDataUrlOfDOArrive[this.currentRowIndexOfCamera] = webcamImage.imageAsDataUrl
    }
  }

  switchToNextCamera(directionOrDeviceId: boolean | string): void {
    // true => move forward through devices
    // false => move backwards through devices
    // string => move to device with given deviceId
    this.nextWebcam.next(directionOrDeviceId);
    this.showCameraLoading()
  }

  private showCameraLoading() {
    this.isCameraLoading = true
  }

  private hideCameraLoading() {
    this.isCameraLoading = false
  }

  //#endregion Camera

  private showCameraErrorMessage(errorMsg: string) {
    errorMsg = this.translateService.instant('BookingArrive.CameraError.CameraUnableToUse')
      + this.translateService.instant('BookingArrive.CameraError.Reason')
      + errorMsg
    this.toastService.error(errorMsg)
  }

  private clearValue() {
    if (this.tabMenuActiveIndex == TabMenu.BookingArrive) {
      this.bookingNoOfBookingArrive = []
      this.photoDataUrlOfBookingArrive = null
    }
    else {
      this.bookingNoOfDOArrive = []
      this.doNos = []
      this.photoDataUrlOfDOArrive = []
    }
  }
}
